import React from 'react';
import { Link } from 'react-router-dom';
import { useSEO } from '../hooks/useSEO';

const Home = () => {
  // Use SEO hook for Home page
  const { seoData } = useSEO('home', {
    h1: 'Test IQ - Kiểm tra chỉ số thông minh trực tuyến',
    metaDescription: 'Làm bài test IQ online miễn phí, kiểm tra chỉ số thông minh EQ, DISC, MBTI và Holland Code. Kết quả chi tiết và chính xác nhất.',
    title: 'Test IQ Online - Kiểm tra chỉ số thông minh miễn phí'
  });

  const tests = [
    {
      id: 'mbti',
      title: 'Test MBTI',
      description: 'Khám phá tính cách của bạn với 16 loại tính cách MBTI',
      questions: '70 câu hỏi',
      time: '30 phút',
      gradient: 'from-blue-500 to-purple-600',
      icon: '🧠',
      link: '/test-mbti'
    },
    {
      id: 'iq',
      title: 'Test IQ',
      description: 'Đánh giá chỉ số thông minh của bạn',
      questions: '40 câu hỏi',
      time: '45 phút',
      gradient: 'from-green-500 to-teal-600',
      icon: '💡',
      link: '/test-iq'
    },
    {
      id: 'eq',
      title: 'Test EQ',
      description: 'Đo lường trí tuệ cảm xúc của bạn',
      questions: '60 câu hỏi',
      time: '25 phút',
      gradient: 'from-purple-500 to-pink-600',
      icon: '❤️',
      link: '/test-eq'
    },
    {
      id: 'disc',
      title: 'Test DISC',
      description: 'Hiểu về phong cách hành vi và giao tiếp',
      questions: '24 câu hỏi',
      time: '15 phút',
      gradient: 'from-red-500 to-orange-600',
      icon: '⚡',
      link: '/test-disc'
    },
    {
      id: 'holland',
      title: 'Holland Codes',
      description: 'Tìm hiểu về sở thích nghề nghiệp',
      questions: '90 câu hỏi',
      time: '20 phút',
      gradient: 'from-yellow-500 to-orange-500',
      icon: '🎯',
      link: '/holland-codes'
    }
  ];

  const stats = [
    { number: '100K+', label: 'Người dùng' },
    { number: '50K+', label: 'Bài test hoàn thành' },
    { number: '99%', label: 'Độ chính xác' },
    { number: '24/7', label: 'Hỗ trợ' }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50 to-purple-100">
      {/* Hero Section */}
      <div className="relative overflow-hidden">
        {/* Decorative background elements */}
        <div className="absolute inset-0">
          <div className="absolute top-20 left-10 w-32 h-32 bg-blue-200/30 rounded-full blur-xl"></div>
          <div className="absolute top-40 right-20 w-48 h-48 bg-purple-200/30 rounded-full blur-xl"></div>
          <div className="absolute bottom-20 left-1/3 w-40 h-40 bg-pink-200/30 rounded-full blur-xl"></div>
        </div>

        <div className="relative container mx-auto px-4 py-20">
          <div className="text-center max-w-4xl mx-auto">
            <div className="mb-6">
              <span className="inline-block px-4 py-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white text-sm font-semibold rounded-full mb-6">
                ✨ Nền tảng test tâm lý hàng đầu Việt Nam
              </span>
            </div>
            
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold mb-6">
              <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                {seoData.h1}
              </span>
            </h1>
            
            <p className="text-xl md:text-2xl text-gray-600 mb-12 leading-relaxed">
              Hiểu rõ tính cách, khả năng và định hướng tương lai thông qua 
              <br className="hidden md:block" />
              các bài test chuẩn quốc tế được tin dùng bởi hàng triệu người
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-16">
              <Link 
                to="/test-mbti"
                className="group inline-flex items-center justify-center px-8 py-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white font-bold rounded-xl hover:shadow-lg hover:scale-105 transition-all duration-300"
              >
                <span>Bắt đầu Test MBTI</span>
                <svg className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>
              </Link>
              <Link 
                to="/about"
                className="inline-flex items-center justify-center px-8 py-4 border-2 border-gray-300 text-gray-700 font-semibold rounded-xl hover:border-blue-500 hover:text-blue-600 transition-all duration-300"
              >
                Tìm hiểu thêm
              </Link>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 max-w-3xl mx-auto">
              {stats.map((stat, index) => (
                <div key={index} className="text-center">
                  <div className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent mb-2">
                    {stat.number}
                  </div>
                  <div className="text-gray-600 font-medium">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Tests Section */}
      <div className="container mx-auto px-4 py-20">
        <div className="text-center mb-16">
          <span className="inline-block px-4 py-2 bg-blue-100 text-blue-700 text-sm font-semibold rounded-full mb-4">
            🎯 Tìm hiểu bản thân
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-4">
            Các Bài Test Phổ Biến
          </h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Chọn bài test phù hợp để khám phá tính cách và tiềm năng của bạn
          </p>
        </div>

        {/* Featured Test (MBTI) */}
        <div className="mb-16">
          <div className="bg-gradient-to-r from-blue-600 to-purple-700 rounded-3xl p-8 md:p-12 text-white relative overflow-hidden">
            {/* Background decoration */}
            <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full -translate-y-32 translate-x-32"></div>
            <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/5 rounded-full translate-y-24 -translate-x-24"></div>
            
            <div className="relative z-10 grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
              <div>
                <div className="flex items-center mb-4">
                  <span className="text-4xl mr-4">🧠</span>
                  <span className="bg-white/20 px-3 py-1 rounded-full text-sm font-semibold">Phổ biến nhất</span>
                </div>
                <h3 className="text-3xl md:text-4xl font-bold mb-4">Test MBTI</h3>
                <p className="text-blue-100 text-lg mb-6 leading-relaxed">
                  Khám phá tính cách của bạn với 16 loại tính cách MBTI. 
                  Bài test được tin dùng bởi hàng triệu người trên thế giới.
                </p>
                <div className="flex items-center space-x-6 mb-8">
                  <div className="flex items-center">
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <span className="font-semibold">70 câu hỏi</span>
                  </div>
                  <div className="flex items-center">
                    <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span className="font-semibold">30 phút</span>
                  </div>
                </div>
                <Link 
                  to="/test-mbti"
                  className="inline-flex items-center px-8 py-4 bg-white text-blue-600 font-bold rounded-xl hover:bg-gray-50 transition-all duration-300 hover:scale-105"
                >
                  Bắt đầu Test MBTI
                  <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                  </svg>
                </Link>
              </div>
              <div className="hidden lg:block">
                <div className="relative">
                  <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
                    <div className="text-center mb-4">
                      <div className="text-6xl mb-2">📊</div>
                      <h4 className="text-xl font-bold">16 Loại Tính Cách</h4>
                    </div>
                    <div className="grid grid-cols-4 gap-2 text-xs">
                      {['INTJ', 'INTP', 'ENTJ', 'ENTP', 'INFJ', 'INFP', 'ENFJ', 'ENFP', 
                        'ISTJ', 'ISFJ', 'ESTJ', 'ESFJ', 'ISTP', 'ISFP', 'ESTP', 'ESFP'].map(type => (
                        <div key={type} className="bg-white/20 rounded px-2 py-1 text-center font-semibold">
                          {type}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Other Tests Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
          {tests.slice(1).map((test, index) => (
            <div key={test.id} className="group">
              <div className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100 group-hover:-translate-y-2">
                {/* Icon và title */}
                <div className="text-center mb-6">
                  <div className={`w-16 h-16 bg-gradient-to-r ${test.gradient} rounded-2xl flex items-center justify-center text-2xl mx-auto mb-4 group-hover:scale-110 transition-transform duration-300`}>
                    {test.icon}
                  </div>
                  <h3 className="text-xl font-bold text-gray-800 mb-2">{test.title}</h3>
                  <p className="text-gray-600 text-sm leading-relaxed h-12 overflow-hidden">
                    {test.description}
                  </p>
                </div>

                {/* Test info compact */}
                <div className="flex justify-center space-x-4 mb-6 text-xs">
                  <span className="bg-gray-100 px-3 py-1 rounded-full text-gray-600">
                    {test.questions}
                  </span>
                  <span className="bg-gray-100 px-3 py-1 rounded-full text-gray-600">
                    {test.time}
                  </span>
                </div>

                {/* CTA Button */}
                <Link 
                  to={test.link}
                  className={`block w-full bg-gradient-to-r ${test.gradient} text-white font-semibold py-3 px-4 rounded-xl text-center hover:shadow-lg transition-all duration-300 text-sm`}
                >
                  Bắt đầu ngay
                </Link>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Features Section */}
      <div className="relative py-24 overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 bg-gradient-to-br from-gray-50 to-blue-50">
          <div className="absolute inset-0">
            <div className="absolute top-0 left-0 w-96 h-96 bg-blue-200/30 rounded-full -translate-x-48 -translate-y-48 blur-3xl"></div>
            <div className="absolute bottom-0 right-0 w-96 h-96 bg-purple-200/30 rounded-full translate-x-48 translate-y-48 blur-3xl"></div>
            <div className="absolute top-1/2 left-1/2 w-64 h-64 bg-pink-200/20 rounded-full -translate-x-32 -translate-y-32 blur-2xl"></div>
          </div>
        </div>

        <div className="relative container mx-auto px-4">
          {/* Header */}
          <div className="text-center mb-20">
            <div className="inline-flex items-center px-6 py-3 bg-white/80 backdrop-blur-sm rounded-full border border-gray-200 mb-6">
              <span className="text-2xl mr-2">🏆</span>
              <span className="text-gray-700 font-semibold">Tại sao chọn chúng tôi?</span>
            </div>
            <h2 className="text-4xl md:text-6xl font-bold mb-6">
              <span className="bg-gradient-to-r from-gray-800 via-blue-700 to-purple-700 bg-clip-text text-transparent">
                Trải Nghiệm
              </span>
              <br />
              <span className="text-gray-800">Khác Biệt</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Hơn 100,000+ người đã tin tưởng lựa chọn KiểmTraNăngLực để khám phá bản thân
            </p>
          </div>

          {/* Stats Bar */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-20">
            {[
              { number: '100K+', label: 'Người dùng', icon: '👥' },
              { number: '99%', label: 'Độ chính xác', icon: '🎯' },
              { number: '16', label: 'Loại tính cách', icon: '🧩' },
              { number: '24/7', label: 'Hỗ trợ', icon: '🚀' }
            ].map((stat, index) => (
              <div key={index} className="group">
                <div className="bg-white/70 backdrop-blur-sm rounded-2xl p-6 text-center border border-gray-200 hover:bg-white hover:shadow-lg transition-all duration-300 hover:-translate-y-1">
                  <div className="text-3xl mb-2">{stat.icon}</div>
                  <div className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent mb-1">
                    {stat.number}
                  </div>
                  <div className="text-gray-600 font-medium text-sm">{stat.label}</div>
                </div>
              </div>
            ))}
          </div>

          {/* Features Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left: Features List */}
            <div className="space-y-8">
              {[
                {
                  icon: '🌍',
                  title: 'Chuẩn Quốc Tế',
                  description: 'Được phát triển theo tiêu chuẩn quốc tế với độ chính xác cao, được chứng nhận bởi các tổ chức tâm lý học hàng đầu thế giới.',
                  gradient: 'from-blue-500 to-cyan-500'
                },
                {
                  icon: '⚡',
                  title: 'Tức Thì & Chính Xác',
                  description: 'Kết quả được phân tích bằng AI và hiển thị ngay lập tức với báo cáo chi tiết về tính cách, điểm mạnh và hướng phát triển.',
                  gradient: 'from-green-500 to-emerald-500'
                },
                {
                  icon: '🔒',
                  title: 'Bảo Mật Tuyệt Đối',
                  description: 'Thông tin được mã hóa SSL 256-bit, tuân thủ GDPR và không bao giờ chia sẻ dữ liệu cá nhân với bên thứ ba.',
                  gradient: 'from-purple-500 to-pink-500'
                },
                {
                  icon: '🎓',
                  title: 'Khoa Học & Tin Cậy',
                  description: 'Dựa trên nghiên cứu của Carl Jung và được kiểm chứng bởi hàng triệu người dùng trên toàn thế giới trong hơn 50 năm.',
                  gradient: 'from-orange-500 to-red-500'
                }
              ].map((feature, index) => (
                <div key={index} className="group flex items-start space-x-4">
                  <div className={`flex-shrink-0 w-16 h-16 bg-gradient-to-r ${feature.gradient} rounded-2xl flex items-center justify-center text-2xl group-hover:scale-110 transition-transform duration-300`}>
                    {feature.icon}
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-gray-800 mb-2 group-hover:text-gray-900 transition-colors">
                      {feature.title}
                    </h3>
                    <p className="text-gray-600 leading-relaxed">
                      {feature.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            {/* Right: Visual Element */}
            <div className="relative">
              <div className="relative bg-white/80 backdrop-blur-sm rounded-3xl p-8 shadow-2xl border border-gray-200">
                {/* Floating elements */}
                <div className="absolute -top-4 -right-4 w-24 h-24 bg-gradient-to-r from-blue-400 to-purple-500 rounded-2xl rotate-12 opacity-80"></div>
                <div className="absolute -bottom-4 -left-4 w-20 h-20 bg-gradient-to-r from-green-400 to-cyan-500 rounded-2xl -rotate-12 opacity-80"></div>
                
                <div className="relative z-10">
                  <div className="text-center mb-6">
                    <div className="text-6xl mb-4">🎯</div>
                    <h3 className="text-2xl font-bold text-gray-800 mb-2">Độ Chính Xác Cao</h3>
                    <p className="text-gray-600">Được tin dùng bởi</p>
                  </div>
                  
                  {/* Progress bars */}
                  <div className="space-y-4">
                    {[
                      { label: 'Độ chính xác', value: 99, color: 'bg-blue-500' },
                      { label: 'Độ tin cậy', value: 97, color: 'bg-green-500' },
                      { label: 'Hài lòng', value: 95, color: 'bg-purple-500' }
                    ].map((metric, index) => (
                      <div key={index}>
                        <div className="flex justify-between text-sm mb-1">
                          <span className="text-gray-700 font-medium">{metric.label}</span>
                          <span className="text-gray-600">{metric.value}%</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-3">
                          <div 
                            className={`h-3 ${metric.color} rounded-full transition-all duration-1000 ease-out`}
                            style={{ width: `${metric.value}%` }}
                          ></div>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="mt-6 text-center">
                    <p className="text-sm text-gray-500">
                      <span className="font-bold text-blue-600">100,000+</span> người dùng hài lòng
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="bg-gray-50 py-20">
        <div className="container mx-auto px-4 text-center">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-6">
              Sẵn Sàng Khám Phá Bản Thân?
            </h2>
            <p className="text-xl text-gray-600 mb-10">
              Hàng triệu người đã tin tưởng lựa chọn. Hãy bắt đầu hành trình khám phá bản thân của bạn ngay hôm nay!
            </p>
            <Link 
              to="/test-mbti"
              className="inline-flex items-center justify-center px-10 py-5 bg-gradient-to-r from-blue-600 to-purple-600 text-white font-bold text-lg rounded-xl hover:shadow-xl hover:scale-105 transition-all duration-300"
            >
              Bắt đầu Test MBTI miễn phí
              <svg className="w-6 h-6 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Home; 